import type { Dict } from './index';

const en: Dict = {
    common: {
        copied: 'Copied!', copy: 'Copy', amount: 'Amount', amountPlaceholder: '0.00', refresh: 'Refresh',
        edit: 'Change', add: 'Add', tryAgain: 'Try again'
    },
    nav: { home: 'Home', deposit: 'Deposit', withdraw: 'Withdraw', history: 'History', account: 'Account' },
    gate: {
        channel: {
            title: 'Join our channel',
            text: 'To use the app, join the official channel first. That is where we post news, bonuses and notices.',
            join: 'Join the channel', check: 'I joined, check',
            error: 'We could not check right now. Try again in a moment.'
        }
    },
    home: {
        hello: 'Hello', registerText: 'Create your {{brand}} account in seconds and deposit with PIX or crypto.',
        createAccount: 'Create my account', hintPix: 'Instant PIX', hintSafe: 'Secure', hintBonus: 'Bonuses',
        welcomeBack: 'Welcome back', balance: 'Your balance', recent: 'Recent activity', seeAll: 'See all',
        tiles: { history: 'History', referrals: 'Referrals', giftCodes: 'Codes', support: 'Support' },
        playNow: 'Play now on the website', playNowHint: 'Log in with your username and password',
        giftTitle: 'Get {{amount}} free', giftText: 'Create your account now: the bonus lands in your balance right away.'
    },
    needAccount: { title: 'Create your account first', text: 'You need an account to deposit, withdraw and see your history.' },
    register: {
        stepTerms: 'Terms', stepAccount: 'Account', stepDone: 'Done',
        termsTitle: 'Terms and conditions', termsSubtitle: 'Read them before creating your account',
        agree: 'I have read and accept the terms and conditions.', accept: 'Accept and continue',
        accountTitle: 'Choose your username', accountSubtitle: 'You log in to the website with it',
        usernamePlaceholder: 'e.g. lucas2024',
        usernameRules: '4 to 20 characters, starting with a letter. Letters and numbers only.',
        passwordInfo: 'Your password is generated for you and shown on the next screen.',
        referralPending: 'Referral code {{code}} will be applied to your account.',
        create: 'Create account', creating: 'Creating your account…',
        successTitle: 'Account created!', successText: 'Keep your login details safe.',
        saveCredentials: 'Save your username and password: you need them to log in to the website. They are also under Account.',
        giftDeposited: '🎁 Your {{amount}} welcome bonus is already in your balance.',
        giftPending: '🎁 Your {{amount}} welcome bonus: use the code {{code}} under Codes.',
        referralApplied: 'Referral code {{code}} applied.',
        depositNow: 'Make my first deposit', openSite: 'Open the website'
    },
    terms: {
        transfer: { title: '🛑 Deposit and withdrawal rules', text: 'Using deposits and withdrawals to move money between different payment methods is prohibited. Any attempt to bypass this rule leads to the balance being frozen or confiscated.' },
        multiple: { title: '⚠️ Multiple accounts', text: 'Owning more than one account breaks the rules and leads to all linked accounts being banned and their balances frozen, without exception.' },
        referral: { title: '🚨 Referral system', text: 'Any attempt to cheat or create multiple accounts to exploit referrals leads to immediate suspension of the account and removal of its referrals.' }
    },
    account: {
        pageTitle: 'My account', title: 'Website login details', username: 'Username', password: 'Password', playerId: 'Player ID',
        showPassword: 'Show', hidePassword: 'Hide', openSite: 'Log in to the website', language: 'Language'
    },
    methods: {
        deposit: { title: 'Deposit', subtitle: 'Choose how to deposit', none: 'No deposit method is available right now.' },
        withdraw: { title: 'Withdraw', subtitle: 'Choose how to withdraw', none: 'No withdrawal method is available right now.' },
        limits: 'Min {{min}} · Max {{max}}', fee: '{{percent}}% fee'
    },
    kinds: {
        pix: 'Instant PIX', crypto: 'Cryptocurrencies', usdt: 'USDT on the BEP20 network', link: 'Payment page',
        card: 'Credit / debit card', manual: 'Reviewed by our team', other: ''
    },
    deposit: {
        limits: 'Limits', rate: 'Rate', youGet: 'Added to your balance', bonus: 'Deposit bonus', creating: 'Creating…',
        pix: { cta: 'Create PIX', instant: 'Pay in your banking app: the balance arrives automatically.' },
        crypto: { coin: 'Coin', cta: 'Get address' },
        usdt: {
            address: 'Your USDT (BEP20) address', min: 'Minimum deposit',
            warning: 'Send only USDT on BNB Smart Chain (BEP20). Any other coin or network will be lost.',
            auto: 'This address is yours alone and never expires. Any amount from the minimum is credited automatically after the network confirmations.'
        },
        link: { cta: 'Go to payment' },
        card: { cta: 'Pay by card' }
    },
    pix: {
        title: 'PIX details', payer: 'Payer', payee: 'Recipient', pixKey: 'PIX key', notSet: 'Not set yet',
        profileIntro: 'PIX needs your details once. They are saved for your next deposits and withdrawals.',
        withdrawIntro: 'Withdrawals are paid by PIX to your CPF. Enter the account holder\'s full name and CPF.',
        cpfKeyHint: 'Your CPF must be registered as a PIX key at your bank.',
        fillField: 'Fill in: {{field}}', save: 'Save details',
        fields: { name: 'Full name', cpf: 'CPF', email: 'E-mail', phone: 'Phone', birthdate: 'Date of birth', city: 'City' },
        placeholders: { name: 'As on your ID', cpf: '000.000.000-00', email: 'you@email.com', phone: '(11) 91234-5678', birthdate: 'DD/MM/YYYY', city: 'e.g. São Paulo' },
        invalid: {
            name: 'Enter your first and last name.', cpf: 'Invalid CPF. Check the numbers.', email: 'Invalid e-mail.',
            phone: 'Invalid phone number.', birthdate: 'Invalid date (DD/MM/YYYY, over 18).', city: 'Invalid city.'
        }
    },
    withdraw: {
        available: 'Available balance', limits: 'Limits', fee: 'Fee', youReceive: 'You receive', debit: 'Taken from the balance',
        approvalNote: 'Withdrawals are reviewed by our team and paid once approved. You get a message in the bot.',
        confirm: 'Confirm the {{amount}} withdrawal via {{method}}?\nYou receive {{net}}.', cta: 'Request withdrawal',
        crypto: { coin: 'Coin', address: '{{coin}} address ({{network}})', warning: 'Check the address and the {{network}} network: a wrong transfer cannot be undone.' }
    },
    status: { pending: 'Pending', confirmed: 'Completed', rejected: 'Declined' },
    tx: {
        type: { deposit: 'Deposit', withdraw: 'Withdrawal' },
        explain: {
            deposit: {
                pending: 'Waiting for your payment. Once it is confirmed, the balance arrives automatically and you get a message in the bot.',
                confirmed: 'Payment confirmed and balance credited. Good luck! 🍀',
                rejected: 'This deposit was not completed. {{reason}}'
            },
            withdraw: {
                pending: 'Withdrawal received. Our team is reviewing it and you will be told in the bot.',
                confirmed: 'Withdrawal approved and sent.',
                rejected: 'This withdrawal was declined. {{reason}}'
            }
        },
        reasons: {
            cancelled_by_user: 'You cancelled the request.', deposit_window_expired: 'The payment time ran out.',
            fulgurpay_failed: 'The payment failed.', fulgurfix_failed: 'The PIX failed.',
            fulgurfix_payout_failed: 'The PIX payout failed and the amount went back to your balance.', fulgurpay_payout_failed: 'The transfer failed and the amount went back to your balance.',
            apay_payout_failed: 'The transfer failed and the amount went back to your balance.', zerone_payout_failed: 'The transfer failed and the amount went back to your balance.',
            zicharge_payout_failed: 'The transfer failed and the amount went back to your balance.'
        },
        payPix: 'Pay with PIX', openPayment: 'Open payment page',
        sendExactly: 'Send exactly ({{coin}})', address: 'Address ({{network}})',
        cryptoWarning: 'Send only {{coin}} on the {{network}} network. The balance arrives automatically after the confirmations.',
        expiresIn: 'Expires in {{minutes}} min', expired: 'The payment time is over.',
        credit: 'Added to balance', debit: 'Taken from balance', bonus: 'Bonus', fee: 'Fee', youReceive: 'You receive',
        destination: 'Destination', date: 'Date', processed: 'Processed', operationId: 'Operation ID',
        cancel: 'Cancel request', cancelConfirm: 'Cancel this request? If you already paid, the amount is still credited.',
        allRequests: 'See all requests'
    },
    history: { title: 'History', subtitle: 'Your deposits and withdrawals', empty: 'No requests yet.', more: 'Load more' },
    referrals: {
        title: 'Referrals', subtitle: 'Invite friends and earn', inactive: 'The referral programme is paused right now.',
        howTitle: 'How it works',
        how: 'Share your link. When a friend creates an account through it and deposits, you earn {{you}}% of the deposit and your friend gets {{friend}}%.',
        total: 'Invited', completed: 'Active', rewards: 'Earned', code: 'Your code', link: 'Your link', share: 'Share link',
        shareText: 'Come play on {{brand}}! Create your account with my link:'
    },
    gift: {
        title: 'Gift codes', subtitle: 'Redeem your code', text: 'Type the code you received to add its value to your balance.',
        placeholder: 'CODE', redeem: 'Redeem', success: '🎉 {{amount}} added to your balance!',
        waitDays: 'You can redeem another code in {{days}} day(s).', waitHours: 'You can redeem another code in {{hours}} hour(s).',
        waitMinutes: 'You can redeem another code in {{minutes}} minute(s).'
    },
    support: {
        title: 'Support', subtitle: 'Talk to our team', telegram: 'Telegram support', whatsapp: 'WhatsApp',
        faq: 'FAQ', site: 'Official website'
    },
    screens: {
        banned: { title: 'Access blocked', text: 'Your account has been blocked. Contact support if you think this is a mistake.' },
        maintenance: { title: 'Under maintenance', text: 'We are improving the app. Come back in a few minutes.' },
        openInTelegram: { title: 'Open it in Telegram', text: 'This app runs inside Telegram. Open the official bot and tap the app button.' },
        expired: { title: 'Session expired', text: 'Close the app and open it again from the bot.' },
        offline: { title: 'No connection', text: 'We could not reach the server. Check your internet and try again.' }
    },
    errors: {
        generic: 'Something went wrong. Try again in a moment.', network_error: 'No connection. Check your internet.', timeout: 'The server took too long. Try again.',
        unknown_error: 'Something went wrong. Try again.', unauthorized: 'Open the app from the Telegram bot.', session_expired: 'Session expired: open the app again.',
        banned: 'Your account is blocked.', maintenance: 'Under maintenance. Come back in a few minutes.', bad_request: 'Invalid request.', server_error: 'Server error. Try again.',
        not_found: 'Not found.', no_account: 'Create your account first.', method_unavailable: 'This payment method is not available right now.',
        amount_invalid: 'Enter a valid amount.', amount_range: 'The amount must be between {{min}} and {{max}}.',
        cooldown: 'You cancelled a request a moment ago. Try this method again in {{minutes}} min.',
        pix_profile_required: 'Complete your PIX details first.', profile_invalid: 'Check the details you entered.',
        create_failed: 'The payment could not be created. Try again in a moment.',
        create_failed_config: 'This payment method is temporarily unavailable. The team has been notified.',
        create_failed_unknown: 'No answer from the provider. Check your History before trying again.',
        create_failed_amount: 'The provider did not accept this amount. Try another amount.',
        create_failed_dailyLimit: 'Daily card limit reached. Try tomorrow or use another method.',
        create_failed_belowMin: 'Amount below the card minimum.', create_failed_aboveMax: 'Amount above the card maximum.',
        create_failed_countryBlocked: 'This card is not accepted. Try another card or method.', create_failed_routing: 'Card payment is not available for this amount right now.',
        invalid_coin: 'Choose a coin.', address_failed: 'Your address could not be created right now. Try again.',
        cannot_cancel: 'This request can no longer be cancelled.', pending_withdrawal: 'You already have a pending withdrawal. Please wait for the review.',
        withdraw_gap: 'You must wait {{hours}} h between two withdrawals. Try again in {{minutes}} min.',
        balance_unavailable: 'Your balance could not be read right now. Try again.',
        insufficient: 'Insufficient balance. Your balance: {{balance}} · needed: {{required}}.',
        daily_limit: 'Withdrawal limit reached ({{limit}} every {{hours}} h). Available now: {{remaining}}.',
        invalid_address: 'Invalid {{coin}} address for the {{network}} network.', field_required: 'Fill in every required field.', field_invalid: 'One of the fields is invalid.',
        invalid_username: 'Invalid username: 4 to 20 letters and numbers, starting with a letter.', busy: 'Please wait, still processing.',
        already_registered: 'You already have an account.', terms_required: 'Accept the terms first.', duplicate_login: 'This username is taken. Choose another one.',
        link_failed: 'Your account was created, but linking it failed. Support has been notified.',
        giftcode_invalid: 'Invalid, expired or already used code.', giftcode_wait: 'Please wait before redeeming another code.',
        giftcode_player_not_found: 'Account not found. Contact support.', giftcode_unavailable: 'Redeeming is unavailable right now. Try later.',
        giftcode_unknown: 'We could not confirm the redemption. Support will check it.', giftcode_credit_failed: 'The amount could not be added. Try again.'
    }
};

export default en;
